// Supersample edges even on 1× displays, but bound GPU memory on large/Retina
// viewports. These are presentation settings, not sensor/image source quality.
export const sceneQualityProfiles = Object.freeze({
  high: Object.freeze({ minRatio: 2, maxRatio: 3, nativeScale: 1.25, maxPixels: 6000000, shadowSize: 2048 }),
  economical: Object.freeze({ minRatio: 1, maxRatio: 1.5, nativeScale: 1, maxPixels: 2000000, shadowSize: 1024 }),
});

const positive = (value, fallback) => Number.isFinite(value) && value > 0 ? value : fallback;

export function sceneRenderQuality({ width, height, devicePixelRatio = 1, quality = "high", maxTextureSize = 4096 } = {}) {
  const profile = sceneQualityProfiles[quality] || sceneQualityProfiles.high;
  const w = Math.max(1, Math.floor(positive(width, 640))), h = Math.max(1, Math.floor(positive(height, 540)));
  const dimensionLimit = Math.max(1, Math.min(4096, Math.floor(positive(maxTextureSize, 4096))));
  const desiredRatio = Math.min(profile.maxRatio, Math.max(profile.minRatio, positive(devicePixelRatio, 1) * profile.nativeScale));
  const pixelRatio = Math.min(desiredRatio, Math.sqrt(profile.maxPixels / (w * h)), dimensionLimit / w, dimensionLimit / h);
  const shadowSize = 2 ** Math.floor(Math.log2(Math.min(profile.shadowSize, dimensionLimit)));
  return { width: w, height: h, pixelRatio, shadowSize, maxPixels: profile.maxPixels, dimensionLimit };
}

export function applySceneRenderQuality(renderer, sun, options) {
  const quality = sceneRenderQuality({ ...options, maxTextureSize: renderer.capabilities?.maxTextureSize });
  // setPixelRatio() resizes using the PREVIOUS CSS dimensions. Updating both
  // together avoids a transient huge buffer when shrinking a large viewport.
  renderer.setDrawingBufferSize(quality.width, quality.height, quality.pixelRatio);
  if (sun.shadow.mapSize.x !== quality.shadowSize) {
    // Three recreates the shadow render target lazily after changing its size.
    sun.shadow.map?.dispose(); sun.shadow.map = null;
    sun.shadow.mapSize.set(quality.shadowSize, quality.shadowSize);
    sun.shadow.needsUpdate = true;
  }
  return quality;
}
